/**
 * Realtime dispatcher — single global WebSocket per browser session.
 *
 * Connects to ``/ws/org/`` (handled by ``apps.office.consumers.OrgEventsConsumer``),
 * receives JSON messages of the shape ``{ type: '<event>', ...payload }``,
 * and re-emits them as ``ws:<event>`` ``CustomEvent``s on ``document``.
 *
 * Per-page modules subscribe with::
 *
 *     document.addEventListener('ws:schedule_changed', () => { ... });
 *     document.addEventListener('ws:email_received', (e) => {
 *         const { unread_count } = e.detail;
 *         ...
 *     });
 *
 * Behaviour
 * ---------
 * - One connection per session, idempotent (a second ``bootstrapRealtime()``
 *   call is a no-op).
 * - Auto-reconnect with exponential backoff (1 s → 30 s cap).
 * - Malformed payloads are silently dropped — never throws.
 * - Anonymous users never connect at all: ``bootstrapRealtime()`` is a
 *   no-op unless ``<body data-realtime="1">`` is present (set by
 *   base.html for authenticated users only). This keeps public pages
 *   (login, signup, password reset) free of the rejected-handshake
 *   reconnect loop and its console noise. Logging in navigates to a
 *   full page render with the flag set, so the socket opens then.
 * - On every reconnect AFTER the initial successful connect, a
 *   ``ws:reconnected`` ``CustomEvent`` is dispatched on ``document``.
 *   Any broadcasts the server sent while the socket was disconnected
 *   (e.g. a cleaner-mobile replay landing during a brief office-side
 *   network blip) were dropped by Channels — the group send is
 *   fire-and-forget — so subscribers MUST treat reconnect as
 *   "something may have changed, refetch your view". This is the
 *   push-based replacement for periodic polling: the office calendar /
 *   team view subscribe to ``ws:reconnected`` and trigger the same
 *   debounced refetch they use for ``ws:schedule_changed``. Result:
 *   no polling, and offline-replayed cleaner actions surface on the
 *   office side within the WS reconnect delay (1-30 s) regardless of
 *   which side was offline. The first ``onopen`` does NOT fire
 *   ``ws:reconnected`` because there is no "gap" to catch up on.
 *
 * See ``docs/REALTIME_WEBSOCKET.md`` for the full design and how
 * to add new event types.
 */

let _bootstrapped = false;
let _ws = null;
let _reconnectDelay = 1000;
let _hadFirstOpen = false;
const _MAX_DELAY = 30000;

function getWsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/org/`;
}

function scheduleReconnect() {
  setTimeout(() => {
    _reconnectDelay = Math.min(_reconnectDelay * 2, _MAX_DELAY);
    connect();
  }, _reconnectDelay);
}

function connect() {
  try {
    _ws = new WebSocket(getWsUrl());
  } catch {
    scheduleReconnect();
    return;
  }

  _ws.onopen = () => {
    _reconnectDelay = 1000;
    if (_hadFirstOpen) {
      document.dispatchEvent(new CustomEvent('ws:reconnected'));
    } else {
      _hadFirstOpen = true;
    }
  };

  _ws.onmessage = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }
    if (!data || typeof data.type !== 'string') return;
    document.dispatchEvent(new CustomEvent(`ws:${data.type}`, { detail: data }));
  };

  _ws.onclose = () => {
    _ws = null;
    scheduleReconnect();
  };

  _ws.onerror = () => {
    if (_ws) _ws.close();
  };
}

export function bootstrapRealtime() {
  if (_bootstrapped) return;
  // Anonymous visitor (login/signup/reset pages): the consumer would
  // reject the handshake anyway — don't open a doomed reconnect loop.
  if (document.body.dataset.realtime !== '1') return;
  _bootstrapped = true;
  connect();
}

if (typeof window !== 'undefined') {
  window.bootstrapRealtime = bootstrapRealtime;
}
